import { useQuery } from '@apollo/client'
import * as React from 'react'
import {
    Box,
    Button,
    Heading,
    HStack,
    Link,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    ModalCloseButton,
    Spinner,
    Text,
    Alert,
    AlertIcon
} from '@chakra-ui/react'
import { BiWindow } from 'react-icons/bi'
import getCampaigns from 'graphql/queries/getCampaigns'
import MaterialTable, { Icons } from 'material-table'
import { tableIcons } from 'components/common/MaterialIcons'
import CampaignDetails from './deets'
import { Campaign } from 'graphql/types'
import { EditCampaign } from './edit'

const Campaigns = () => {
    const { loading, error, data, refetch } = useQuery(getCampaigns, { fetchPolicy: 'no-cache' })
    const [editCampaign, setEditCampaign] = React.useState<Campaign | null>(null)

    const refetchCampaigns = () => refetch()

    const createNewCampaign = () => {
        setEditCampaign({
            ID: '',
            Priority: '',
            Name: '',
            Channel: '',
            Notes: '',
            DateCreated: new Date().toISOString(),
            DateCompleted: null,
            DateDrafted: null,
            TotalPushes: '0',
            Message: '',
            Title: '',
            Description: '',
            Status: 'Draft',
            Logic: '',
            Doc: '',
            Values: ''
        })
    }

    if (loading) {
        return (
            <Box p={8} textAlign="center">
                <Spinner size="xl" />
                <Text mt={4}>Loading campaigns...</Text>
            </Box>
        )
    }

    if (error) {
        return (
            <Alert status="error" m={4}>
                <AlertIcon />
                Error loading campaigns: {error.message}
            </Alert>
        )
    }

    const campaigns = data?.getCampaigns || []

    const tableData = campaigns.map((campaign: Campaign) => ({
        ID: campaign.ID,
        DateCreated: new Date(campaign.DateCreated).toLocaleString(),
        DateCompleted: campaign.DateCompleted,
        DateDrafted: campaign.DateDrafted,
        TotalPushes: campaign.TotalPushes,
        Message: campaign.Message,
        Title: campaign.Title,
        Description: campaign.Description,
        Status: campaign.Status,
        Logic: campaign.Logic,
        Doc: campaign.Doc,
        Values: campaign.Values,
        Name: campaign.Name,
        Priority: Number(campaign.Priority),
        Clicks: campaign.Clicks
    }))

    return (
        <Box p={6}>
            <HStack spacing={4} mb={6} align="center">
                <BiWindow size={35} />
                <Heading size="lg">Campaigns</Heading>
            </HStack>

            <Box mb={4}>
                <Link
                    href="https://docs.google.com/spreadsheets/d/1qL3Hq6yo6JdMFEMOC-EE3NKIssr1jb9IN1_41fY6uvQ/edit?gid=0#gid=0"
                    isExternal
                    color="blue.500"
                    fontWeight="medium"
                >
                    Open master sheet
                </Link>
            </Box>

            <Box mb={4}>
                <Button colorScheme="blue" onClick={createNewCampaign}>
                    Create New Campaign
                </Button>
            </Box>

            <Modal isOpen={!!editCampaign} onClose={() => setEditCampaign(null)} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalCloseButton />
                    <ModalBody>
                        {editCampaign && (
                            <EditCampaign
                                Campaign={editCampaign}
                                setEditCampaign={setEditCampaign}
                            />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            <Box bg="white" borderRadius="md" shadow="sm">
                <MaterialTable
                    icons={tableIcons as Icons}
                    title=""
                    columns={[
                        { title: 'ID', field: 'ID' },
                        { title: 'Name', field: 'Name' },
                        { title: 'Priority', field: 'Priority' },
                        { title: 'Description', field: 'Description' },
                        { title: 'Status', field: 'Status' }
                    ]}
                    data={tableData}
                    options={{
                        showTitle: false,
                        sorting: true,
                        filtering: true,
                        search: true,
                        pageSize: 50
                    }}
                    detailPanel={(rowData) => (
                        <CampaignDetails
                            Campaign={rowData as Campaign}
                            refetchCampaigns={refetchCampaigns}
                            setEditCampaign={setEditCampaign}
                        />
                    )}
                />
            </Box>
        </Box>
    )
}

export default Campaigns
